/* eslint-disable no-console */
import React, { lazy } from 'react'
import type { ComponentType } from 'react'
import type { CmsBlock, Scalars } from '@generated/graphql'
import { useCampaignContext } from 'src/contexts/campaign-context'
import lazyRetry from 'src/utils/lazyRetry'
import { sliceSections } from 'src/utils/sliceSections'

import FullwidthStaticBanner from './sections/FullwidthStaticBanner'
import FullwidthCarousel from './sections/FullwidthCarousel'
import AnimatedCarousel from './sections/AnimatedCarousel'
import LiveShopping from './sections/LiveShopping'

const AfterGallery = lazy(() =>
  lazyRetry(() => import('./sections/AfterGallery'), 'AfterGallery')
)

const BiggerTitleAndSubtitle = lazy(() =>
  lazyRetry(
    () => import('./sections/BiggerTitleAndSubtitlte'),
    'BiggerTitleAndSubtitle'
  )
)

const BrandsSlider = lazy(() =>
  lazyRetry(
    () => import('./sections/BrandsSlider/BrandsSlider'),
    'BrandsSlider'
  )
)

const CampaignSections = lazy(() =>
  lazyRetry(() => import('./sections/CampaignSections'), 'CampaignSections')
)

const CampaignFold = lazy(() =>
  lazyRetry(() => import('./sections/CampaignFold'), 'CampaignFold')
)

const CatalogDoubleFullImages = lazy(() =>
  lazyRetry(
    () => import('./sections/CatalogDoubleFullImages/CatalogDoubleFullImages'),
    'CatalogDoubleFullImages'
  )
)

const CatalogTripleImagesCarousel = lazy(() =>
  lazyRetry(
    () => import('./sections/CatalogTripleImagesCarousel'),
    'CatalogTripleImagesCarousel'
  )
)

const Carousel = lazy(() =>
  lazyRetry(() => import('./sections/Carousel'), 'Carousel')
)

const CheckBalance = lazy(() =>
  lazyRetry(() => import('./sections/CheckBalance'), 'CheckBalance')
)

const CMSButton = lazy(() =>
  lazyRetry(() => import('./sections/CMSButton'), 'CMSButton')
)

const CollectionCarousel = lazy(() =>
  lazyRetry(() => import('./sections/CollectionCarousel'), 'CollectionCarousel')
)

const CollectionCarouselWithDescription = lazy(() =>
  lazyRetry(
    () => import('./sections/CollectionCarouselWithDescription'),
    'CollectionCarouselWithDescription'
  )
)

const CollectionSlider = lazy(() =>
  lazyRetry(
    () => import('./sections/CollectionSlider/CollectionSlider'),
    'CollectionSlider'
  )
)

const DepartmentBanner = lazy(() =>
  lazyRetry(() => import('./sections/DepartmentBanner'), 'DepartmentBanner')
)

const DoubleBanner = lazy(() =>
  lazyRetry(() => import('./sections/DoubleBanner'), 'DoubleBanner')
)

const DoubleIcons = lazy(() =>
  lazyRetry(() => import('./sections/DoubleIcons/DoubleIcons'), 'DoubleIcons')
)

const DoubleImageGallery = lazy(() =>
  lazyRetry(
    () => import('./sections/DoubleImageGallery/DoubleImageGallery'),
    'DoubleImageGallery'
  )
)

const ExpansibleText = lazy(() =>
  lazyRetry(() => import('./sections/ExpansibleText'), 'ExpansibleText')
)

const ExpansibleQuestions = lazy(() =>
  lazyRetry(
    () => import('./sections/ExpansibleQuestions'),
    'ExpansibleQuestions'
  )
)

const FAQ = lazy(() => lazyRetry(() => import('./sections/Faq/FAQ'), 'FAQ'))

const FaqBox = lazy(() =>
  lazyRetry(() => import('./sections/FaqBox'), 'FaqBox')
)

const FiveImageGallery = lazy(() =>
  lazyRetry(() => import('./sections/FiveImageGallery'), 'FiveImageGallery')
)

const FiveMediaGallery = lazy(() =>
  lazyRetry(() => import('./sections/FiveMediaGallery'), 'FiveMediaGallery')
)

const FormAllIn = lazy(() =>
  lazyRetry(() => import('./sections/FormAllIn/FormAllIn'), 'FormAllIn')
)

const FormAllInAfterSale = lazy(() =>
  lazyRetry(
    () => import('./sections/FormAllInAfterSale/FormAllInAfterSale'),
    'FormAllInAfterSale'
  )
)

const FormAllInB2B = lazy(() =>
  lazyRetry(
    () => import('./sections/FormAllInB2B/FormAllInB2B'),
    'FormAllInB2B'
  )
)

const FullwidthNarrowBanner = lazy(() =>
  lazyRetry(
    () => import('./sections/FullwidthNarrowBanner'),
    'FullwidthNarrowBanner'
  )
)

const GaleryLiveDisplay = lazy(() =>
  lazyRetry(() => import('./sections/GaleryLiveDisplay'), 'GaleryLiveDisplay')
)

const GenerateLiveloCookie = lazy(() =>
  lazyRetry(
    () => import('./sections/GenerateLiveloCookie'),
    'GenerateLiveloCookie'
  )
)

const GridCollection = lazy(() =>
  lazyRetry(() => import('./sections/GridCollection'), 'GridCollection')
)

const GridCollectionWithFilter = lazy(() =>
  lazyRetry(
    () =>
      import('./sections/GridCollectionWithFilter/GridCollectionWithFilter'),
    'GridCollectionWithFilter'
  )
)

const IframeReader = lazy(() =>
  lazyRetry(() => import('./sections/IframeReader'), 'IframeReader')
)

const ImageBannerWithTexts = lazy(() =>
  lazyRetry(
    () => import('./sections/ImageBannerWithTexts'),
    'ImageBannerWithTexts'
  )
)

const ImageTextImage = lazy(() =>
  lazyRetry(() => import('./sections/ImageTextImage'), 'ImageTextImage')
)

const InstitutionalTitleandSubtitle = lazy(() =>
  lazyRetry(
    () => import('./sections/InstitutionalTitleandSubtitle'),
    'InstitutionalTitleandSubtitle'
  )
)

const KitsCarousel = lazy(() =>
  lazyRetry(() => import('./sections/KitsCarousel'), 'KitsCarousel')
)

const LiveAndCollection = lazy(() =>
  lazyRetry(() => import('./sections/LiveAndCollection'), 'LiveAndCollection')
)

const MinimalistShowcase = lazy(() =>
  lazyRetry(
    () => import('./sections/MinimalistShowcase/MinimalistShowcase'),
    'MinimalistShowcase'
  )
)

const MosaicGridCollection = lazy(() =>
  lazyRetry(
    () => import('./sections/MosaicGridCollection'),
    'MosaicGridCollection'
  )
)

const NavigationCategoryWithTitleImage = lazy(() =>
  lazyRetry(
    () => import('./sections/NavigationCategoryWithTitleImage'),
    'NavigationCategoryWithTitleImage'
  )
)

const NarrowBannerCarousel = lazy(() =>
  lazyRetry(
    () => import('./sections/NarrowBannerCarousel'),
    'NarrowBannerCarousel'
  )
)

const NavigationDepartment = lazy(() =>
  lazyRetry(
    () => import('./sections/NavigationDepartment'),
    'NavigationDepartment'
  )
)

const NavigationTabDisplay = lazy(() =>
  lazyRetry(
    () => import('./sections/NavigationTabDisplay'),
    'NavigationTabDisplay'
  )
)

const NavigationChains = lazy(() =>
  lazyRetry(() => import('./sections/NavigationChains'), 'NavigationChains')
)

const NavigationCollectionCarouselColors = lazy(() =>
  lazyRetry(
    () => import('./sections/NavigationCollectionCarouselColors'),
    'NavigationCollectionCarouselColors'
  )
)

const NavigationSubcategory = lazy(() =>
  lazyRetry(
    () => import('./sections/NavigationSubcategory'),
    'NavigationSubcategory'
  )
)

const NavigationMomentsIcons = lazy(() =>
  lazyRetry(
    () => import('./sections/NavigationMomentsIcons'),
    'NavigationMomentsIcons'
  )
)

const NavigationCategory = lazy(() =>
  lazyRetry(() => import('./sections/NavigationCategory'), 'NavigationCategory')
)

const NavigationSwitch = lazy(() =>
  lazyRetry(() => import('./sections/NavigationSwitch'), 'NavigationSwitch')
)

const NavigationCollection = lazy(() =>
  lazyRetry(
    () => import('./sections/NavigationCollection'),
    'NavigationCollection'
  )
)

const NavigationImages = lazy(() =>
  lazyRetry(
    () => import('./sections/NavigationImages/NavigationImages'),
    'NavigationImages'
  )
)

const NavigationButtons = lazy(() =>
  lazyRetry(() => import('./sections/NavigationButtons'), 'NavigationButtons')
)

const NavigationText = lazy(() =>
  lazyRetry(
    () => import('./sections/NavigationText/NavigationText'),
    'NavigationText'
  )
)

const NavigattionTabs = lazy(() =>
  lazyRetry(() => import('./sections/NavigattionTabs'), 'NavigattionTabs')
)

const NavigationImagesButtons = lazy(() =>
  lazyRetry(
    () => import('./sections/NavigationImagesButtons/NavigationImagesButtons'),
    'NavigationImagesButtons'
  )
)

const NewExpansibleText = lazy(() =>
  lazyRetry(() => import('./sections/NewExpansibleText'), 'NewExpansibleText')
)

const ReadMore = lazy(() =>
  lazyRetry(() => import('./sections/ReadMore/ReadMore'), 'ReadMore')
)

const RichText = lazy(() =>
  lazyRetry(() => import('./sections/RichText'), 'RichText')
)

const RichTextFeatured = lazy(() =>
  lazyRetry(() => import('./sections/RichTextFeatured'), 'RichTextFeatured')
)

const RichTextLife = lazy(() =>
  lazyRetry(() => import('./sections/RichTextLife'), 'RichTextLife')
)

const ShowcaseBanner = lazy(() =>
  lazyRetry(() => import('./sections/ShowcaseBanner'), 'ShowcaseBanner')
)

const SpaceBetweenSections = lazy(() =>
  lazyRetry(
    () => import('./sections/SpaceBetweenSections'),
    'SpaceBetweenSections'
  )
)

const StaticBanner = lazy(() =>
  lazyRetry(() => import('./sections/StaticBanner'), 'StaticBanner')
)

const StorieAndTexts = lazy(() =>
  lazyRetry(() => import('./sections/StorieAndTexts'), 'StorieAndTexts')
)

const TextAnchor = lazy(() =>
  lazyRetry(() => import('./sections/TextAnchor'), 'TextAnchor')
)

const ThemeNavigation = lazy(() =>
  lazyRetry(() => import('./sections/ThemeNavigation'), 'ThemeNavigation')
)

const ThreeCards = lazy(() =>
  lazyRetry(() => import('./sections/ThreeCards'), 'ThreeCards')
)

const ThreeMediaGallery = lazy(() =>
  lazyRetry(() => import('./sections/ThreeMediaGallery'), 'ThreeMediaGallery')
)

const TripleBannerCarousel = lazy(() =>
  lazyRetry(
    () => import('./sections/TripleBannerCarousel'),
    'TripleBannerCarousel'
  )
)

const TripleImageGallery = lazy(() =>
  lazyRetry(() => import('./sections/TripleImageGallery'), 'TripleImageGallery')
)

const VideoCollection = lazy(() =>
  lazyRetry(() => import('./sections/VideoCollection'), 'VideoCollection')
)

const VideoFullScreen = lazy(() =>
  lazyRetry(() => import('./sections/VideoFullScreen'), 'VideoFullScreen')
)

const VideoWithCTAFullScreen = lazy(() =>
  lazyRetry(
    () => import('./sections/VideoWithCTAFullScreen/VideoWithCTAFullScreen'),
    'VideoWithCTAFullScreen'
  )
)

const Advantages = lazy(() =>
  lazyRetry(() => import('./sections/Advantages/Advantages'), 'Advantages')
)

const ImageVideoAndText = lazy(() =>
  lazyRetry(() => import('./sections/ImageVideoAndText'), 'ImageVideoAndText')
)

const InsiderShelf = lazy(() =>
  lazyRetry(() => import('./sections/InsiderShelf'), 'InsiderShelf')
)

/**
 * Sections: Components imported from '../components/sections' only.
 * Do not import or render components from any other folder in here.
 */
const COMPONENTS: Record<
  string,
  ComponentType<Scalars['JSONPropsCmsObject']>
> = {
  Carousel,
  DoubleBanner,
  StaticBanner,
  NavigationCollection,
  NavigationCategory,
  NavigationMomentsIcons,
  NavigationSwitch,
  ShowcaseBanner,
  CollectionCarousel,
  CollectionSlider,
  ThreeMediaGallery,
  NavigationImages,
  NavigationButtons,
  NavigationText,
  NavigattionTabs,
  NavigationCollectionCarouselColors,
  ThemeNavigation,
  InstitutionalTitleandSubtitle,
  VideoCollection,
  RichText,
  RichTextLife,
  AfterGallery,
  ImageTextImage,
  ExpansibleText,
  ExpansibleQuestions,
  MosaicGridCollection,
  VideoFullScreen,
  FullwidthStaticBanner,
  FormAllIn,
  FormAllInAfterSale,
  DoubleImageGallery,
  TripleImageGallery,
  GenerateLiveloCookie,
  FiveImageGallery,
  NavigationChains,
  FiveMediaGallery,
  SpaceBetweenSections,
  RichTextFeatured,
  FullwidthCarousel,
  TextAnchor,
  ReadMore,
  ThreeCards,
  CollectionCarouselWithDescription,
  GridCollection,
  IframeReader,
  FAQ,
  NavigationImagesButtons,
  BiggerTitleAndSubtitle,
  FullwidthNarrowBanner,
  KitsCarousel,
  TripleBannerCarousel,
  NavigationDepartment,
  VideoWithCTAFullScreen,
  NavigationCategoryWithTitleImage,
  NarrowBannerCarousel,
  NavigationTabDisplay,
  NavigationSubcategory,
  LiveAndCollection,
  GaleryLiveDisplay,
  FormAllInB2B,
  DepartmentBanner,
  CampaignSections,
  CampaignFold,
  MinimalistShowcase,
  DoubleIcons,
  ImageBannerWithTexts,
  CatalogDoubleFullImages,
  CatalogTripleImagesCarousel,
  StorieAndTexts,
  BrandsSlider,
  CheckBalance,
  FaqBox,
  NewExpansibleText,
  AnimatedCarousel,
  GridCollectionWithFilter,
  CMSButton,
  Advantages,
  ImageVideoAndText,
  LiveShopping,
  InsiderShelf
}

interface Props {
  sections?: Array<{ name: string; data: CmsBlock['data'] }>
  fold: string
  isAfterGallery?: boolean
  quantityRender?: number
}

function RenderCMS({
  sections,
  fold,
  isAfterGallery,
  quantityRender = 3,
}: Props) {
  const { campaignPage, setCampaignPage } = useCampaignContext()
  const hasToggle = sections?.some((e) => e.name === 'CampaignSections')
  const campaignIndex = sections?.findIndex((e) => e.name === 'CampaignFold')

  const localTheme = sessionStorage?.getItem('campaignTheme')

  setCampaignPage(hasToggle && localTheme === 'true')

  const campaignSections =
    campaignIndex && campaignIndex !== -1
      ? sliceSections(campaignIndex, campaignPage, sections)
      : sections

  return (
    <>
      {campaignSections?.map(({ name, data }, index) => {
        const Component = COMPONENTS[name]

        if (!Component) {
          console.error(`Could not find component for block ${name}. Add a new component
            for this block or remove it from the CMS`)

          return null
        }

        if (isAfterGallery) {
          return (
            <Component key={`cms-section-${index}0 after-gallery`} {...data} />
          )
        }

        return fold === 'above'
          ? index <= quantityRender && (
              <Component
                key={`cms-section-${index}`}
                {...data}
                fold="above"
                imageLoading="eager"
              />
            )
          : index > quantityRender && (
              <Component key={`cms-section-${index}`} {...data} />
            )
      })}
    </>
  )
}

export default RenderCMS
